import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import useTranslation from '@/hooks/useTranslation';
import { useMutateSetQualityOverride } from '@/reactQuery/post';
import { useCurrentConversations } from '@/hooks/useCurrentConversations';
import { AllTranscriptsProps, ConversationQualitySignals } from '@/types';

interface QualityOverrideDialogProps {
  conversation: AllTranscriptsProps;
}

const CATEGORIES: Array<'EXCELLENT' | 'GOOD' | 'LOW'> = ['EXCELLENT', 'GOOD', 'LOW'];

const categoryStyles: Record<string, string> = {
  EXCELLENT:
    'border-emerald-200 data-[selected=true]:bg-emerald-50 data-[selected=true]:text-emerald-700 data-[selected=true]:border-emerald-400',
  GOOD: 'border-neutral-200 data-[selected=true]:bg-neutral-100 data-[selected=true]:text-neutral-800 data-[selected=true]:border-neutral-400',
  LOW: 'border-red-200 data-[selected=true]:bg-red-50 data-[selected=true]:text-red-700 data-[selected=true]:border-red-400',
};

const categoryLabelKeys: Record<string, string> = {
  EXCELLENT: 'msg.qualityExcellent',
  GOOD: 'msg.qualityGood',
  LOW: 'msg.qualityLow',
};

/**
 * TELV1-433 — researcher quality override.
 *
 * Category-only by design: the AI's raw signals (engagement, verdict,
 * reason) stay immutable as provenance; the researcher flips the
 * actionable bucket and must say why. Every change — including a reset
 * to the AI judgement — is appended to the rubric's override history
 * server-side.
 */
function QualityOverrideDialog({ conversation }: QualityOverrideDialogProps) {
  const { lang } = useTranslation();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<'EXCELLENT' | 'GOOD' | 'LOW' | null>(null);
  const [reason, setReason] = useState('');
  const { mutateAsync: setOverride, isPending } = useMutateSetQualityOverride();
  const {
    completedConversations,
    selectedTranscript,
    actions: { setCompletedConversations, setSelectedTranscript },
  } = useCurrentConversations();

  const { quality } = conversation;
  if (quality?.status !== 'complete') return null;

  const conversationId = conversation.conversation_id ?? conversation._id;

  const openDialog = () => {
    setCategory(quality.category ?? null);
    setReason('');
    setOpen(true);
  };

  // Patch both store surfaces so the badge updates everywhere without a
  // refetch (react-countup-style animation concerns don't apply here, but
  // the list page cache does).
  const applyToStore = (nextQuality: ConversationQualitySignals) => {
    setCompletedConversations(
      completedConversations.map((c) =>
        c._id === conversation._id ? { ...c, quality: nextQuality } : c,
      ),
    );
    if (selectedTranscript && selectedTranscript._id === conversation._id) {
      setSelectedTranscript({ ...selectedTranscript, quality: nextQuality });
    }
  };

  const submit = async (nextCategory: 'EXCELLENT' | 'GOOD' | 'LOW' | null) => {
    try {
      const response = await setOverride({
        projectId: conversation.project_id,
        conversationId,
        category: nextCategory,
        reason: reason.trim(),
      });
      applyToStore((response as { data: { quality: ConversationQualitySignals } }).data.quality);
      setOpen(false);
    } catch {
      toast.error(lang.get('msg.somethingWentWrong'));
    }
  };

  const changed = category !== quality.category;
  const canSave = !!category && changed && reason.trim().length > 0;
  const canReset = !!quality.overridden && reason.trim().length > 0;

  return (
    <>
      <button
        type="button"
        aria-label={lang.get('msg.adjustQuality')}
        className="p-1 text-neutral-400 hover:text-neutral-700 transition-colors"
        onClick={openDialog}
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{lang.get('msg.adjustQuality')}</DialogTitle>
            <DialogDescription>
              {lang.get('msg.qualityAIJudged')}:{' '}
              {lang.get(categoryLabelKeys[quality.ai_category ?? quality.category ?? 'GOOD'])}
              {quality.reason ? ` — ${quality.reason}` : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                data-selected={category === c}
                className={cn(
                  'flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors',
                  categoryStyles[c],
                )}
                onClick={() => setCategory(c)}
              >
                {lang.get(categoryLabelKeys[c])}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="quality-override-reason" className="text-sm text-neutral-600">
              {lang.get('msg.qualityOverrideReason')}
            </label>
            <Textarea
              id="quality-override-reason"
              value={reason}
              maxLength={500}
              rows={3}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            {quality.overridden ? (
              <Button
                variant="outline"
                disabled={!canReset || isPending}
                onClick={() => submit(null)}
              >
                {lang.get('msg.qualityOverrideResetToAI')}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="outline" disabled={isPending} onClick={() => setOpen(false)}>
                {lang.get('msg.cancel')}
              </Button>
              <Button disabled={!canSave || isPending} onClick={() => submit(category)}>
                {lang.get('msg.save')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default QualityOverrideDialog;
